const SCRIPT_ID = "google-recaptcha-v3"

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>
    }
  }
}

let loadPromise: Promise<void> | null = null

/**
 * Injects the Google reCAPTCHA v3 script once and resolves when the API is
 * ready. Safe to call multiple times and on the server (no-op).
 */
export function loadReCaptcha(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve()
  const siteKey = process.env.RECAPTCHA_KEY
  if (!siteKey) return Promise.resolve()

  if (!loadPromise) {
    loadPromise = new Promise<void>((resolve, reject) => {
      const onReady = () => window.grecaptcha?.ready(() => resolve())
      if (window.grecaptcha) {
        onReady()
        return
      }
      let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement
      if (!script) {
        script = document.createElement("script")
        script.id = SCRIPT_ID
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
        script.async = true
        document.head.appendChild(script)
      }
      script.addEventListener("load", onReady)
      script.addEventListener("error", () => {
        loadPromise = null
        reject(new Error("Failed to load reCAPTCHA script"))
      })
    })
  }
  return loadPromise
}

/**
 * Executes reCAPTCHA v3 and returns a fresh token for the given action
 * (tokens expire after two minutes, so one is generated per submit).
 * Returns an empty string when reCAPTCHA is unavailable (missing site key
 * or blocked script) — the server rejects invalid/missing tokens.
 */
export async function getReCaptchaToken(action: string): Promise<string> {
  if (typeof window === "undefined") return ""
  const siteKey = process.env.RECAPTCHA_KEY
  if (!siteKey) return ""
  try {
    await loadReCaptcha()
    return (await window.grecaptcha?.execute(siteKey, { action })) ?? ""
  } catch {
    return ""
  }
}
